import type * as React from 'react';

import { Alert, AlertTitle, Box, Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ReauthPromptProps {
  onContinue: () => void;
  // We have evidence the login page is not reachable — a round trip that came
  // back too fast to have been a human, or three prompts. Say so HERE rather
  // than switching the app to an error pane, which would take the page — and
  // the unsaved text — away.
  escalated?: boolean;
}

// Shown INSTEAD of a silent redirect when the session died while the user had
// unsaved edits on screen (LOCKED plan W1/A.3 + E).
//
// DELIBERATELY NOT A `<Dialog>`. The whole point of leaving the page mounted is
// that the user can take their unsaved text with them before the redirect
// discards it — and a MUI Modal defeats exactly that: its backdrop swallows
// pointer events (no selecting, no dragging) and `ModalManager` marks every
// sibling `aria-hidden`. The affordance would collapse into "retype it from
// memory". So this is a plain fixed banner: no backdrop, no focus trap, no
// aria-hidden on the app. `pointerEvents: 'none'` on the positioning layer lets
// clicks and text selection reach the form underneath; the banner itself takes
// them back with `'auto'`.
//
// It never auto-dismisses — it stays until the user chooses to continue.
//
// THIS COMPONENT MOVES FOCUS NOWHERE. Not to the button, not to the banner.
// Both were tried and both were wrong, for two DIFFERENT reasons — recorded
// here so nobody reaches for a third variant:
//
//   1. `autoFocus` on the button. The banner appears unannounced over a live,
//      editable page (a 401 from an autocomplete fires while the user is
//      mid-word). Focusing the button yanks the caret out of their input, and
//      the next keystroke — mid-sentence, so most likely Space or Enter —
//      ACTIVATES it: redirect, text gone, prompt never even read.
//   2. `.focus()` on the Alert container. Safer against activation, but the
//      same theft by a quieter route: it still rips the caret out mid-word,
//      firing RHF's `onBlur` validation in the middle of a value and ending any
//      IME composition — and every keystroke after that lands on a `<div>` and
//      is silently discarded. The user types into nothing until they look up.
//
// Neither buys anything: MUI puts `role="alert"` on the Alert, so assistive
// tech announces the banner on mount WITHOUT any focus movement, and keyboard
// users reach the button with an ordinary Tab. Losing the user's caret is never
// the price worth paying in a component whose entire purpose is not losing
// their work.
//
// The copy deliberately does NOT promise the edits will be preserved (plan
// W1/E, variant (a)) — the redirect discards them and a prompt that claimed
// otherwise would be a lie.
export const ReauthPrompt = ({
  onContinue,
  escalated = false,
}: ReauthPromptProps): React.JSX.Element => {
  const { t } = useTranslation('auth');
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: theme.spacing(2),
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        px: 2,
        zIndex: theme.zIndex.snackbar,
        pointerEvents: 'none',
      }}
    >
      <Alert
        severity="warning"
        elevation={6}
        sx={{
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: 560,
        }}
      >
        <AlertTitle>{t('reauth.promptTitle')}</AlertTitle>
        {t('reauth.promptBody')}
        {escalated ? (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
            {t('reauth.promptUnavailable')}
          </Typography>
        ) : null}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onContinue}
            sx={{ minHeight: 44 }}
          >
            {t('reauth.promptAction')}
          </Button>
        </Box>
      </Alert>
    </Box>
  );
};
